import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ResponsiveSection from "@/components/layout/ResponsiveSection";
import ContactForm from "@/components/ContactForm";
import { motion } from "framer-motion";
import { ScrollAnimatedSection } from "@/components/shared/ScrollAnimatedSection";
import { GlowCard } from "@/components/shared/GlowCard";
import { InteractiveCard } from "@/components/ui/interactive-card";

import { FileText, Check, AlertCircle, Clock, Award, Zap, Shield, ArrowRight, Stethoscope, Heart, Building2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const Inhaltsversicherung = () => {
  const stats = [
    { icon: Clock, value: "24/7", label: "Schutz" },
    { icon: Award, value: "Neuwert", label: "Entschädigung" },
    { icon: Zap, value: "Schnell", label: "Regulierung" },
    { icon: Shield, value: "Weltweit", label: "Schutz" }
  ];

  return <div className="min-h-screen bg-background">
      <div className="relative z-10">
        <Navigation />

      {/* Hero Section */}
      <ResponsiveSection spacing="lg">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-accent/5 -z-10" />
          <ScrollAnimatedSection>
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-accent text-background">Empfohlen</Badge>
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 break-words"
              >
                Inhaltsversicherung für Arztpraxen
              </motion.h1>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
                Umfassender Schutz für Ihre Praxiseinrichtung und medizinisches Equipment
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {stats.map((stat, idx) => (
                <GlowCard key={idx} to="#" index={idx}>
                  <stat.icon className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
                  <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground">{stat.label}</div>
                </GlowCard>
              ))}
            </div>
          </ScrollAnimatedSection>
        </div>
      </ResponsiveSection>

      {/* Leistungsübersicht */}
      <section className="py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/[0.02] to-background" />
        <ResponsiveSection spacing="lg">
          <ScrollAnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-center text-foreground break-words">
              Leistungsübersicht
            </h2>
            <p className="text-sm text-muted-foreground mb-12 text-center">
              Umfassender Schutz für Ihre gesamte Praxisausstattung
            </p>

            <div className="glas-cards grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 xl:gap-16">
              
              <div className="relative pb-6 md:pb-0">
                <div className="relative z-10">
                  <div className="flex flex-col lg:flex-row items-center gap-3 mb-3 lg:mb-2">
                    <Shield className="w-8 h-8 lg:w-5 lg:h-5 text-accent" />
                    <h3 className="text-lg sm:text-xl font-bold text-foreground text-center lg:text-left">
                      Versicherte Gefahren
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 lg:mb-6 text-center lg:text-left">
                    Umfassender Schutz vor den wichtigsten Risiken
                  </p>
                  
                  <div className="list-container">
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Feuer, Blitzschlag, Explosion</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Einbruchdiebstahl und Vandalismus</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Leitungswasser und Rohrbruch</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Sturm und Hagel</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Überspannung durch Blitz</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Elementarschäden (optional)</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="relative z-10">
                  <div className="flex flex-col lg:flex-row items-center gap-3 mb-3 lg:mb-2">
                    <FileText className="w-8 h-8 lg:w-5 lg:h-5 text-accent" />
                    <h3 className="text-lg sm:text-xl font-bold text-foreground text-center lg:text-left">
                      Versicherte Gegenstände
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 lg:mb-6 text-center lg:text-left">
                    Schutz für Ihre gesamte Praxisausstattung
                  </p>
                  
                  <div className="list-container">
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Medizinische Geräte und Instrumente</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Praxismöbel und Einrichtung</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">IT-Hardware und Software</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Verbrauchsmaterialien und Arzneimittel</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Patientenunterlagen und Akten</span>
                    </div>
                    <div className="list-item">
                      <span className="checkmark">
                        <Check className="w-5 h-5 text-accent" />
                      </span>
                      <span className="list-text">Kunst- und Dekorationsgegenstände</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollAnimatedSection>
        </ResponsiveSection>
      </section>

      {/* Besondere Vorteile */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-accent/10" />
        <ResponsiveSection spacing="lg">
          <ScrollAnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center text-foreground break-words">
              Besondere Vorteile
            </h2>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glas-cards"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Neuwertentschädigung</h3>
                    <p className="text-xs sm:text-sm text-foreground/70">Ersatz zum Neupreis ohne Abzug für Alter oder Abnutzung</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Grobe Fahrlässigkeit mitversichert</h3>
                    <p className="text-xs sm:text-sm text-foreground/70">Auch bei Unachtsamkeit bleiben Sie geschützt</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Bewegliche Sachen außerhalb</h3>
                    <p className="text-xs sm:text-sm text-foreground/70">Auch bei Hausbesuchen sind Ihre Geräte versichert</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Glasbruch inklusive</h3>
                    <p className="text-xs sm:text-sm text-foreground/70">Fenster, Türen, Vitrinen sind automatisch abgesichert</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Aufräumungs- und Entsorgungskosten</h3>
                    <p className="text-xs sm:text-sm text-foreground/70">Folgekosten nach einem Schaden sind mitversichert</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Wiederherstellung von Daten</h3>
                    <p className="text-xs sm:text-sm text-foreground/70">Elektronische Aufzeichnungen sind geschützt</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </ScrollAnimatedSection>
        </ResponsiveSection>
      </section>

      {/* Für wen geeignet */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center text-foreground break-words">
          Für wen ist die Inhaltsversicherung geeignet?
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <InteractiveCard enableTilt={true} enablePulse={true} enableDynamicShadow={true} className="border-border/50 hover:border-accent/50 text-center">
            <CardHeader>
              <Stethoscope className="w-10 h-10 sm:w-12 sm:h-12 text-accent mb-2 mx-auto" />
              <CardTitle className="text-lg sm:text-xl">Hausarztpraxen</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground/70 text-sm sm:text-base">
                Schutz für Basisausstattung und allgemeinmedizinische Geräte wie EKG, Labor und Ultraschall.
              </p>
            </CardContent>
          </InteractiveCard>

          <InteractiveCard enableTilt={true} enablePulse={true} enableDynamicShadow={true} className="border-border/50 hover:border-accent/50 text-center">
            <CardHeader>
              <Heart className="w-10 h-10 sm:w-12 sm:h-12 text-accent mb-2 mx-auto" />
              <CardTitle className="text-lg sm:text-xl">Fachärzte</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground/70 text-sm sm:text-base">
                Absicherung hochspezialisierter und teurer Medizintechnik wie Röntgengeräte, OP-Ausstattung oder Lasersysteme.
              </p>
            </CardContent>
          </InteractiveCard>

          <InteractiveCard enableTilt={true} enablePulse={true} enableDynamicShadow={true} className="border-border/50 hover:border-accent/50 text-center">
            <CardHeader>
              <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-accent mb-2 mx-auto" />
              <CardTitle className="text-lg sm:text-xl">MVZ & Kliniken</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground/70 text-sm sm:text-base">
                Umfangreiche Deckung für großflächige Einrichtungen mit mehreren Behandlungsräumen und Spezialgeräten.
              </p>
            </CardContent>
          </InteractiveCard>
        </div>
      </ResponsiveSection>

      {/* Schadensbeispiele */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center text-foreground break-words">
          Schadensbeispiele aus der Praxis
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <InteractiveCard className="hover:shadow-[0_0_30px_rgba(139,195,74,0.2)] text-center">
            <div className="glas-cards">
              <AlertCircle className="w-8 h-8 text-accent mb-4 mx-auto" />
              <h3 className="text-base sm:text-lg font-semibold mb-3 break-words">
                Wasserschaden durch Rohrbruch
              </h3>
              <p className="text-foreground/70 mb-4 text-sm sm:text-base">
                Leitungswasserschaden beschädigt Praxismöbel, Elektronik und medizinische Geräte im Wert von 45.000€.
              </p>
              <div className="pt-4 border-t border-border">
                <p className="text-xs sm:text-sm">
                  <strong className="text-accent">Versicherungsleistung:</strong> Vollständig abgedeckt inkl. Neuwerterstattung
                </p>
              </div>
            </div>
          </InteractiveCard>

          <InteractiveCard className="hover:shadow-[0_0_30px_rgba(139,195,74,0.2)] text-center">
            <div className="glas-cards">
              <AlertCircle className="w-8 h-8 text-accent mb-4 mx-auto" />
              <h3 className="text-base sm:text-lg font-semibold mb-3 break-words">
                Einbruch mit Gerätediebstahl
              </h3>
              <p className="text-foreground/70 mb-4 text-sm sm:text-base">
                Einbrecher stehlen medizinische Geräte, IT-Equipment und Medikamente im Gesamtwert von 78.000€.
              </p>
              <div className="pt-4 border-t border-border">
                <p className="text-xs sm:text-sm">
                  <strong className="text-accent">Versicherungsleistung:</strong> Neuwerterstattung aller gestohlenen Gegenstände
                </p>
              </div>
            </div>
          </InteractiveCard>

          <InteractiveCard className="hover:shadow-[0_0_30px_rgba(139,195,74,0.2)] text-center">
            <div className="glas-cards">
              <AlertCircle className="w-8 h-8 text-accent mb-4 mx-auto" />
              <h3 className="text-base sm:text-lg font-semibold mb-3 break-words">
                Blitzschlag Überspannung IT
              </h3>
              <p className="text-foreground/70 mb-4 text-sm sm:text-base">
                Überspannungsschaden durch Blitzeinschlag zerstört gesamte IT-Infrastruktur im Wert von 12.000€.
              </p>
              <div className="pt-4 border-t border-border">
                <p className="text-xs sm:text-sm">
                  <strong className="text-accent">Versicherungsleistung:</strong> Inkl. Datenwiederherstellung vollständig erstattet
                </p>
              </div>
            </div>
          </InteractiveCard>
        </div>
      </ResponsiveSection>

      {/* NEUER CTA-Bereich mit Lead-Formular */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/15 via-accent/5 to-accent/15" />
        
        <motion.div 
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent"
        />
        
        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            
            <div className="text-center md:text-left">
              <h2 className="text-3xl font-bold mb-4">
                Jetzt persönlich beraten lassen
              </h2>
              <p className="text-muted-foreground mb-6">
                Unsere Experten helfen Ihnen, den optimalen Schutz zu finden.
              </p>
              <Link 
                to="/termin-buchen"
                className="inline-flex items-center px-8 py-4 bg-transparent text-foreground border-2 border-accent rounded-md font-semibold transition-all hover:shadow-[0_0_25px_rgba(139,195,74,0.5)] hover:scale-105"
              >
                Jetzt Termin vereinbaren
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
            
            <div className="bg-card/80 backdrop-blur-sm border border-accent/30 rounded-xl p-6 hover:border-accent/50 transition-colors">
              <h3 className="text-xl font-semibold mb-4 text-center">
                Oder hinterlassen Sie uns Ihre Daten
              </h3>
              <ContactForm />
            </div>
            
          </div>
        </div>
      </section>

      <Footer />
      </div>
    </div>;
};
export default Inhaltsversicherung;

